/**
 * Configures the generated @hey-api fetch client.
 *
 * All API calls target `/api/*`. In development, Vite proxies that prefix
 * to the FastAPI backend (see `vite.config.ts`); in production, nginx does
 * the same rewrite (see `nginx.conf`). The browser never knows the backend
 * URL — only the same-origin `/api` path.
 *
 * Importing this module has the side effect of configuring the singleton
 * client. `src/lib/api/index.ts` does this on first import.
 */

import { client } from "@/lib/api/generated/client.gen";
import type { ViewAsState } from "@/lib/api/generated/types.gen";
import { logger } from "@/lib/logger";
import { toast, toastAfterReload } from "@/lib/toast";

/**
 * Structured error thrown by all SDK calls.
 *
 * The generated fetch client with `throwOnError: true` throws just the
 * parsed response body by default, which loses the HTTP status. The error
 * interceptor below wraps every failure in this shape so callers can branch
 * on `status` (e.g. 401 vs 403 vs 400) and read FastAPI's `{ detail: ... }`
 * payload from `body`. `status` is 0 when the failure is not an HTTP error
 * response (see the interceptor).
 */
export interface ApiError {
  status: number;
  body: unknown;
  /** The response's `Retry-After` in seconds, when it gave one (a 429 or a 503). */
  retryAfter?: number;
}

/** A `Retry-After` header as whole seconds; an HTTP date or anything else gives undefined. */
export function retryAfterSeconds(header: string | null): number | undefined {
  if (header === null || !/^\d+$/.test(header.trim())) {
    return undefined;
  }
  return Number(header.trim());
}

/**
 * True for a 401 or 403: the backend refused the session, so the user must
 * sign in again. Every other failure (a 5xx while the backend restarts, a
 * proxy 502, status 0 when it is unreachable) is temporary and worth retrying.
 */
export function isAuthError(error: unknown): boolean {
  const status = (error as Partial<ApiError> | null | undefined)?.status;
  return status === 401 || status === 403;
}

/**
 * The header every request carries while the dashboard shows a view as
 * someone else. A change that carries it while the server no longer honours
 * a view (stopped in another tab, or run out) answers 409 `view_as_ended`,
 * never runs as the developer. Only its presence counts on the server.
 */
export const VIEW_AS_HEADER = "X-View-As";

/** The header's value for a view: `person:<profile id>`, or `role:<role>` for a role view. */
export function viewAsHeaderValue(view: ViewAsState): string {
  return view.kind === "person" && view.person_id
    ? `person:${view.person_id}`
    : `role:${view.role}`;
}

/** The view the latest `/auth/me` answer named, as the header's value; null without one. */
let viewAsHeader: string | null = null;

/**
 * Loads the page again after the view ended elsewhere (Stoppen in another tab,
 * or it ran out), so every query answers as the developer, and says so in a
 * toast once it has.
 */
function reloadAfterViewEnded(): void {
  toastAfterReload("info", { key: "viewAs.toast.ended" });
  window.location.reload();
}

/** The id of the toast a refused change shows, so changes refused together show one. */
const VIEW_AS_READ_ONLY_TOAST = "view-as-read-only";

/** Whether an `/auth/me` answer has told this page whom it shows yet. */
let viewAsKnown = false;

/**
 * Follows who the latest `/auth/me` answer says the dashboard shows: while it
 * names a view, every request carries `X-View-As`. Once the page knows whom it
 * shows, any change of that identity reloads it, because the cached answers of
 * every other query belong to the identity before. An answer without a view
 * after one with a view means the view ended elsewhere (Stoppen in another tab,
 * or it ran out): the page loads again as the developer, and the header stays
 * on until it has, so a change made in between still answers `view_as_ended`.
 * A view started or switched in another tab loads the page again as the new
 * view, with the header already following it, so a change made in between is
 * refused as read-only.
 */
export function followViewAs(view: ViewAsState | null | undefined): void {
  const next = view ? viewAsHeaderValue(view) : null;
  if (!viewAsKnown) {
    viewAsKnown = true;
    viewAsHeader = next;
    return;
  }
  if (next === viewAsHeader) {
    return;
  }
  if (next === null) {
    logger.debug("[view-as]", "the view ended elsewhere, reloading");
    reloadAfterViewEnded();
    return;
  }
  logger.debug("[view-as]", "the view changed elsewhere, reloading");
  viewAsHeader = next;
  toastAfterReload("info", { key: "viewAs.toast.changed" });
  window.location.reload();
}

/** Whether a failed call answered `status` with `detail` as its code. */
function answered(error: unknown, status: number, detail: string): boolean {
  const { status: got, body } = (error ?? {}) as Partial<ApiError>;
  return got === status && (body as { detail?: unknown } | null | undefined)?.detail === detail;
}

/** Whether a failed call says the view this page shows has ended: a 409 `view_as_ended`. */
export function isViewAsEnded(error: unknown): boolean {
  return answered(error, 409, "view_as_ended");
}

/** Whether a failed call is a change refused while the dashboard shows someone else: a 403 `view_as_read_only`. */
export function isViewAsReadOnly(error: unknown): boolean {
  return answered(error, 403, "view_as_read_only");
}

client.setConfig({
  baseUrl: "/api",
  throwOnError: true,
});

client.interceptors.request.use((request) => {
  logger.debug("[api]", request.method, request.url);
  if (viewAsHeader !== null) {
    request.headers.set(VIEW_AS_HEADER, viewAsHeader);
  }
  return request;
});

client.interceptors.response.use((response) => {
  logger.debug("[api]", response.status, response.url);
  return response;
});

// Every failure of a call reaches this interceptor, not only non-OK
// responses. Transport failures (offline, DNS, backend down) and errors
// while building the request arrive without a response; a 2xx whose body
// fails to parse arrives with an OK one. Only a non-OK response carries an
// HTTP error status and a FastAPI body. Everything else becomes status 0
// ("no HTTP error response"): the query client retries it (unlike 401/403),
// and error UIs fall back to the detail message.
// Error interceptors chain: one registered after this receives the ApiError.
client.interceptors.error.use((error, response) => {
  if (response && !response.ok) {
    const retryAfter = retryAfterSeconds(response.headers.get("Retry-After"));
    return {
      status: response.status,
      body: error,
      ...(retryAfter === undefined ? {} : { retryAfter }),
    } satisfies ApiError;
  }
  return {
    status: 0,
    body: { detail: error instanceof Error ? error.message : "Network error" },
  } satisfies ApiError;
});

// The view-as answers are said here, once for every request, in a toast; the
// error still reaches the caller, whose form stays as it was and shows nothing
// of its own (`inlineFailure` in src/lib/api-errors.ts).
// - A change refused because the dashboard shows someone else: an error toast,
//   one for changes refused together. When this page did not know of the view
//   (it started in another tab), the page loads again first, so it shows the
//   bar with Wissel and Stoppen, and the toast says it there.
// - A change refused because the view this page still shows has ended: the
//   page loads again, so the bar goes and the page answers as the developer,
//   and a toast says so after the reload.
client.interceptors.error.use((error) => {
  if (isViewAsEnded(error)) {
    logger.debug("[view-as]", "a change answered view_as_ended, reloading");
    reloadAfterViewEnded();
  } else if (isViewAsReadOnly(error) && viewAsHeader === null) {
    logger.debug("[view-as]", "a change was refused for a view this page did not know, reloading");
    toastAfterReload("error", { key: "errors.codes.view_as_read_only" });
    window.location.reload();
  } else if (isViewAsReadOnly(error)) {
    logger.debug("[view-as]", "a change was refused while viewing");
    toast.error({ key: "errors.codes.view_as_read_only" }, { id: VIEW_AS_READ_ONLY_TOAST });
  }
  return error;
});

export { client };
